import React, { act, Component } from 'react'
import { Popover, PopoverBody, PopoverHeader, Button, Input, Progress, Alert } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import Select from 'react-select'
import moment from 'moment'
import { debounce } from 'lodash'
import { RepsTypeAhead } from '../../TaskManagement/TaskComponents/TypeAheadComponents'

import withApolloClient from '../../withApolloClient'
import DatetimePickerWithTimeZone from "../../Material/DatetimePickerWithTimeZone"
import { PreferredDateTimeContext } from "../../Context/PreferredDateTimeContext"
import { GetConfigurationSettings } from "../../Functions/querys"

const UpdateIncidentMutation = gql`
mutation UpdateIncidentMutation($input: UpdateIncidentMutationInput!) {
  updateIncident(input: $input) {
    ok
    errors {
      messages
    }
  }
}`

const IncidentReportedByChoices = gql`
query IncidentReportedByChoices {
  allIncidentChoices: __type(name: "IncidentsIncidentReportedByChoices") {
    values: enumValues {
      name
      description
    }
  }
}`

const AllCarRentals = gql`query AllCarRentals($carPk: Int!, $activeOn:Date!){
    allRentals(carPk: $carPk, activeOn:$activeOn) {
        edges {
            node {
                id
                startDate
                endDate
                driver {
                    id
                    pk
                    name
                }
            }
        }
    } 
}`

const AllDrivers = gql`query AllDrivers($searchTerm: String, $branchIds: [ID]){
    drivers: allDrivers(searchTerm: $searchTerm, branchIds: $branchIds, first: 10){
      edges{
        node{
          id
          name
          pk
          tlcLicense
          phone
          email
          branch{
            id
            name
          }
        }
      }
    }
}`

class UpdateIncidentInfo extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: props.value || '',
            error: null,
            loading: false,
            driverOptions: [],
            driverLoading: false,
        }

        this.updateDriverSearchInput = debounce(this.fetchDriverOptions, 350)
    }

    componentDidMount() {
        if (this.props.name === 'driverId' && this.props.open) {
            this.fetchDriverOptions('')
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value || prevProps.open !== this.props.open) {
            this.setState({ value: this.props.value || '', error: null })
        }

        if (
            this.props.name === 'driverId' &&
            this.props.open &&
            (!prevProps.open || prevProps.branchId !== this.props.branchId)
        ) {
            this.fetchDriverOptions('')
        }
    }

    componentWillUnmount() {
        if (this.updateDriverSearchInput?.cancel) {
            this.updateDriverSearchInput.cancel()
        }
    }

    fetchDriverOptions = (searchTerm = '') => {
        const { apolloClient, branchId } = this.props

        this.setState({ driverLoading: true })
        apolloClient.query({
            query: AllDrivers,
            variables: {
                searchTerm: searchTerm || null,
                branchIds: branchId ? [branchId] : null,
            },
            fetchPolicy: 'network-only',
        }).then((result) => {
            const driverOptions = (result?.data?.drivers?.edges || []).map(({ node }) => ({
                value: node.id,
                label: node.name || `Driver #${node.pk || node.id}`,
                pk: node.pk,
                phone: node.phone,
                email: node.email,
                tlcLicense: node.tlcLicense,
                branchName: node.branch?.name,
            }))
            this.setState({ driverOptions, driverLoading: false })
        }).catch(() => {
            this.setState({ driverOptions: [], driverLoading: false })
        })
    }

    handleUpdateIncident = () => {
        const { apolloClient, incidentId, name, refetchIncident, handleClose, metadata, type } = this.props
        const { value } = this.state

        this.setState({ loading: true })
        let input = { incidentId }

        if (metadata || type === "json") {
            const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})
            const additionalInfo = parsed.additional_info || {}
            const updatedAdditionalInfo = { ...additionalInfo, [name]: value }
            const updatedMetadata = { ...parsed, additional_info: updatedAdditionalInfo }
            input["metadata"] = JSON.stringify(updatedMetadata)
        } else {
            input[name] = value
        }

        apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: { input }
        }).then(response => {
            if (response.data.updateIncident.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data.updateIncident.errors[0]?.messages.toString() || 'Update failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred. Please try again.' })
        })
    }

    handleDeleteIncidentField = () => {
        const { apolloClient, incidentId, name, refetchIncident, handleClose, metadata } = this.props

        this.setState({ loading: true })

        const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})
        const additionalInfo = parsed.additional_info || {}
        delete additionalInfo[name]

        const updatedMetadata = { ...parsed, additional_info: additionalInfo }

        const input = {
            incidentId,
            metadata: JSON.stringify(updatedMetadata)
        }

        apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: { input }
        }).then(response => {
            if (response.data.updateIncident.ok) {
                refetchIncident()
                handleClose()
                this.setState({ value: '', loading: false, error: null })
            } else {
                const msg = response.data.updateIncident.errors[0]?.messages.toString() || 'Delete failed'
                this.setState({ error: msg, loading: false })
            }
        }).catch(() => {
            this.setState({ loading: false, error: 'An error occurred while deleting. Please try again.' })
        })
    }


    render() {
        const { open, target, handleClose, title, name, allIncidentChoices, type, min, max } = this.props
        const { value, loading, error } = this.state

        const choices = allIncidentChoices?.values ? allIncidentChoices.values.map(item => ({ value: item.name, label: item.description })) : []
        const inputFieldConfig = this.props.incidentMetadataColumnConfigs?.find(f => f.key === name)

        const rentalOptions = (this.props.rentals?.edges || []).map(({ node }) => ({
            value: node.id,
            pk: node.driver.pk ?? node.driver.id,
            name: node.driver.name || 'Unknown',
            start: moment(node.startDate).format('YYYY-MM-DD'),
            end: node.endDate ? moment(node.endDate).format('YYYY-MM-DD') : "ACTIVE",
        }))
        const currentDriverOption = this.props.currentOption && this.props.value ? {
            value: this.props.value,
            ...this.props.currentOption,
        } : null
        const selectedDriverOption = this.state.driverOptions.find((opt) => opt.value === (this.state.value || this.props.value))
            || currentDriverOption
        return (
            <Popover placement="auto-start" isOpen={open} target={target} toggle={handleClose} trigger="legacy" style={{ minWidth: '250px' }}>
                <PopoverHeader onClick={handleClose}>{title}&nbsp;&nbsp;<i className="fa fa-times-circle float-right" /></PopoverHeader>
                <PopoverBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <div className="pb-2">
                        {name === 'notificationDatetime' ? (
                            <PreferredDateTimeContext.Consumer>
                                {({ preferredDateTimeInfo }) => {
                                    const tz = this.props.branchTimezone || preferredDateTimeInfo.timezone
                                    return (
                                        <DatetimePickerWithTimeZone
                                            key={tz}
                                            type={type || 'datetime-local'}
                                            name={name}
                                            timezone={tz}
                                            value={value}
                                            min={min ? moment(min).tz(tz).format(moment.HTML5_FMT.DATETIME_LOCAL) : null}
                                            max={max ? moment(max).tz(tz).format(moment.HTML5_FMT.DATETIME_LOCAL) : null}
                                            setError={(error) => this.setState({ error })}
                                            updateInput={(name, value) => this.setState({ value: value })}
                                        />
                                    )
                                }}
                            </PreferredDateTimeContext.Consumer>
                        ) :
                            name === 'assignedToIds' ? (
                                <RepsTypeAhead onChange={(value) => this.setState({ value: value })} defaultValue={value} permissions={["custom_can_assign_to_incident"]} />
                            ) :
                                name === 'rentalId' ? (
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        options={rentalOptions}
                                        value={rentalOptions?.find(opt => opt.value === (this.state.value || this.props.value)) || null}
                                        formatOptionLabel={(opt) => (<><p className="m-0"><small>({opt.pk})</small> {opt.name}</p><small>{opt.start} — {opt.end}</small></>)}
                                        placeholder="Select Rental Driver"
                                        isLoading={this.props.allRentalsLoading}
                                        onChange={(opt) => this.setState({ value: opt?.value || '' })}
                                    />
                                ) : name === 'driverId' ? (
                                    <Select
                                        className="bos-custom-select"
                                        classNamePrefix="bos-select"
                                        options={this.state.driverOptions}
                                        value={selectedDriverOption || null}
                                        placeholder="Search Driver"
                                        isLoading={this.state.driverLoading}
                                        onMenuOpen={() => {
                                            if (!this.state.driverOptions.length) {
                                                this.fetchDriverOptions('')
                                            }
                                        }}
                                        onInputChange={(inputValue, actionMeta) => {
                                            if (actionMeta.action === 'input-change') {
                                                this.updateDriverSearchInput(inputValue)
                                            }
                                        }}
                                        onChange={(opt) => this.setState({ value: opt?.value || '' })}
                                        formatOptionLabel={(opt) => (
                                            <>
                                                <p className="m-0">
                                                    <small>({opt.pk || '--'})</small> {opt.label}
                                                </p>
                                                <small>
                                                    {[opt.phone, opt.tlcLicense, opt.branchName].filter(Boolean).join(' • ') || opt.email || 'No extra details'}
                                                </small>
                                            </>
                                        )}
                                        noOptionsMessage={() => this.state.driverLoading ? 'Loading drivers...' : 'No drivers found'}
                                        isClearable
                                    />
                                ) :
                                    name === 'reportedBy' ? (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            options={choices}
                                            defaultValue={choices.find(opt => opt.value === value)}
                                            onChange={(opt) => this.setState({ value: opt.value })}
                                        />
                                    ) : type === "json" && inputFieldConfig?.dataType === 'enum' ? (
                                        <Select
                                            className="bos-custom-select"
                                            classNamePrefix="bos-select"
                                            options={inputFieldConfig.options.map(opt => ({ value: opt, label: opt }))}
                                            defaultValue={{ value: value, label: value }}
                                            onChange={(opt) => this.setState({ value: opt.value })}
                                        />
                                    ) : type === "json" && inputFieldConfig?.dataType === 'bool' ? (
                                        <div className="d-flex align-items-center">
                                            <span >Yes</span>
                                            <div className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    className="toggle-switch-checkbox"
                                                    id={`bool-toggle-${name}`}
                                                    checked={!value}
                                                    onChange={(e) => this.setState({ value: !e.target.checked })}
                                                />
                                                <label className="toggle-switch-label" htmlFor={`bool-toggle-${name}`}>
                                                    <span className="toggle-switch-inner" />
                                                    <span className="toggle-switch-switch" />
                                                </label>
                                            </div>
                                            <span >No</span>
                                        </div>
                                    ) :
                                        (<Input
                                            type={type || 'text'}
                                            name={name}
                                            value={value}
                                            onChange={(e) => this.setState({ value: e.target.value })}
                                        />
                                        )}

                        {loading ? (
                            <Progress animated color="info" value={100} />
                        ) : (
                            <div className="d-flex justify-content-between mt-3">
                                {this.props.showDelete && (
                                    <Button color="danger" size="sm" onClick={this.handleDeleteIncidentField}> Delete </Button>
                                )}
                                <Button color="primary" size="sm" onClick={this.handleUpdateIncident}> Submit</Button>
                            </div>
                        )}
                    </div>
                </PopoverBody>
            </Popover>
        )
    }
}

export default graphql(IncidentReportedByChoices, {
    options: { fetchPolicy: 'cache-first' },
    props: ({ data: { allIncidentChoices } }) => ({ allIncidentChoices }),
})(
    graphql(AllCarRentals, {
        options: ({ carPk, incidentDate }) => ({
            variables: { carPk, activeOn: incidentDate },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            skip: !carPk || !incidentDate,
        }),
        props: ({ data }) => ({
            rentals: data?.allRentals,
            allRentalsLoading: data?.loading,
        }),
    })(
        graphql(GetConfigurationSettings, {
            skip: (props) => !props.branchId,
            options: (props) => ({
                variables: { branchId: props.branchId, name: "PREFERRED_TIMEZONE" },
                fetchPolicy: "cache-first",
            }),
            props: ({ data: { getConfigurationSettings } }) => ({
                branchTimezone: getConfigurationSettings,
            }),
        })(withApolloClient(UpdateIncidentInfo))
    )
)
